"use client";

import { useMemo } from "react";

/**
 * Numbered pagination control.
 *
 * Markup and class names match the `.mc-pagination` styles already defined
 * globally in `public/main/css/style.css`, so this renders identically to the
 * hand-rolled copies in the press-release, media-coverage, experiences,
 * testimonials, search and cruises listings.
 */

interface PaginationProps {
  /** 1-based index of the page currently shown. */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Disables every control while a page is in flight. */
  disabled?: boolean;
  /** Names the landmark — set it when a page has more than one pagination. */
  label?: string;
  className?: string;
}

/** Pages either side of the current one that stay visible when truncating. */
const WINDOW = 1;
/** Above this many pages the list is truncated with ellipses. */
const MAX_UNTRUNCATED = 7;

type Entry =
  | { key: string; type: "page"; value: number }
  | { key: string; type: "ellipsis" };

function buildEntries(currentPage: number, totalPages: number): Entry[] {
  const entries: Entry[] = [];
  if (totalPages <= 1) return entries;

  if (totalPages <= MAX_UNTRUNCATED) {
    for (let i = 1; i <= totalPages; i++) {
      entries.push({ key: `p${i}`, type: "page", value: i });
    }
    return entries;
  }

  entries.push({ key: "p1", type: "page", value: 1 });

  // Leading gap only once page 1 and the window are no longer adjacent.
  if (currentPage > WINDOW + 2) {
    entries.push({ key: "el-start", type: "ellipsis" });
  }

  const start = Math.max(2, currentPage - WINDOW);
  const end = Math.min(totalPages - 1, currentPage + WINDOW);
  for (let i = start; i <= end; i++) {
    entries.push({ key: `p${i}`, type: "page", value: i });
  }

  if (currentPage < totalPages - (WINDOW + 1)) {
    entries.push({ key: "el-end", type: "ellipsis" });
  }

  entries.push({ key: `p${totalPages}`, type: "page", value: totalPages });
  return entries;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  label = "Pagination",
  className,
}: PaginationProps) {
  const entries = useMemo(
    () => buildEntries(currentPage, totalPages),
    [currentPage, totalPages],
  );

  // Nothing to page through — render no landmark at all rather than an empty one.
  if (totalPages <= 1) return null;

  const go = (page: number) => {
    if (disabled) return;
    const next = Math.min(Math.max(page, 1), totalPages);
    // Re-selecting the current page would re-fetch and re-scroll for no reason.
    if (next === currentPage) return;
    onPageChange(next);
  };

  return (
    <nav
      className={`mc-pagination ${className ?? ""}`.trim()}
      aria-label={label}
    >
      <button
        type="button"
        className="mc-pagination-btn mc-pagination-prev"
        disabled={currentPage <= 1 || disabled}
        onClick={() => go(currentPage - 1)}
        aria-label="Go to previous page"
      >
        <i className="fa fa-chevron-left" aria-hidden="true"></i>
      </button>

      {entries.map((entry) =>
        entry.type === "ellipsis" ? (
          <span
            key={entry.key}
            className="mc-pagination-ellipsis"
            aria-hidden="true"
          >
            ...
          </span>
        ) : (
          <button
            key={entry.key}
            type="button"
            className={`mc-pagination-btn ${
              currentPage === entry.value ? "mc-pagination-active" : ""
            }`}
            onClick={() => go(entry.value)}
            disabled={disabled}
            aria-label={`Go to page ${entry.value}`}
            aria-current={currentPage === entry.value ? "page" : undefined}
          >
            {entry.value}
          </button>
        ),
      )}

      <button
        type="button"
        className="mc-pagination-btn mc-pagination-next"
        disabled={currentPage >= totalPages || disabled}
        onClick={() => go(currentPage + 1)}
        aria-label="Go to next page"
      >
        <i className="fa fa-chevron-right" aria-hidden="true"></i>
      </button>
    </nav>
  );
}
